import { useEffect, useState } from "react";
import { Coord, GameConfig, Grid } from "../../types/grid";
import { Unit } from "../../types/unit";
import { TASKS } from "../../types/unit";
import Map from "../components/Map";
import { getUnitIdx } from "../components/Map/utils/cellInfo";
import { GetResourceType, SetNewGatherTaskList } from "./utils/resource";
import { processUnits } from "../../logic/units/unitsLogic";
import { Building, ConstructableBuilding } from "../../types/building";
import { MODE } from "../../types/game";
import { RESOURCE_TYPES } from "../../types/resource";
import "./game.css";
import { defaultConfig } from "../Editor/components/ProjectDetail/components/ElementConfiguration/components/MapEditor/components/MapEditorLegacy/MapEditor";

const defaultUnits = [
  {
    id: 1,
    position: {
      x: 11,
      y: 17,
    },
    destination: {
      x: -1,
      y: -1,
    },
    taskStack: [],
    path: {
      coords: [],
      moveDirection: 0,
      currentPosition: 0,
    },
    hp: 10,
    damage: 3,
    defence: 5,
    ticksToMove: 5,
    ticksToGather: 40,
    currentTick: 0,
    resource: {
      ResourceType: 0,
      ResourceCount: 0,
    },
    repoPathTraced: false,
  },
  {
    id: 2,
    position: {
      x: 8,
      y: 3,
    },
    destination: {
      x: -1,
      y: -1,
    },
    taskStack: [],
    path: {
      coords: [],
      moveDirection: 0,
      currentPosition: 0,
    },
    hp: 10,
    damage: 3,
    defence: 5,
    ticksToMove: 5,
    ticksToGather: 40,
    currentTick: 0,
    resource: {
      ResourceType: 0,
      ResourceCount: 0,
    },
    repoPathTraced: false,
  },
];

const buildableBuildings = [
  {
    name: "Barracks",
    cost: 15,
    src: "building_1.png",
    width: 60,
    height: 80,
    isResource: false,
  },
  {
    name: "Blacksmith",
    cost: 27,
    src: "building_3.png",
    width: 50,
    height: 80,
    isResource: false,
  },
  {
    name: "Farm",
    cost: 21,
    src: "building_2_.png",
    width: 45,
    height: 80,
    isResource: true,
    resourceAccepted: RESOURCE_TYPES.ORE,
  },
  {
    name: "Mill",
    cost: 21,
    src: "building_4_.png",
    width: 45,
    height: 45,
    isResource: true,
    resourceAccepted: RESOURCE_TYPES.TIMBER,
  },
];

let defaultUserState = {
  ore: 0,
  timber: 0,
};

const FPS = 30;

export default function Game({ onModeToggle }: { onModeToggle: () => void }) {
  const mapIdx = parseInt(
    localStorage.getItem("attack-on-grandon-map-idx") || "0"
  );
  const [config, setConfig] = useState<GameConfig>(defaultConfig);
  const [userState, setUserState] = useState(defaultUserState);
  const [units, setUnits] = useState<Unit[]>(defaultUnits);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit>();
  const [frame, setFrame] = useState(0);
  const [activeCell, setActiveCell] = useState<Coord>();
  const [selectedContstructableBuilding, setSelectedContstructableBuilding] =
    useState<ConstructableBuilding>();

  useEffect(() => {
    const ls = localStorage.getItem("attack-on-grandon-map");
    if (ls) {
      setConfig(JSON.parse(ls)[mapIdx]);
    } else {
      alert("No map loaded.");
    }
  }, []);

  let grid: Grid = config.grid;

  // if (config && config.grid) {
  //   grid = config.grid;
  // }

  const loop = (f: number) => {
    setTimeout(() => {
      let frame = f > 30 ? 0 : f;
      setFrame(frame);

      if (grid.length > 1) {
        let result = processUnits(
          grid,
          units,
          buildings,
          defaultUserState,
          frame
        );
        setUnits(result.units);
        defaultUserState = result.userState;
      }

      loop(frame + 1);
    }, 1000 / FPS);
  };

  useEffect(() => {
    loop(0);
  }, [config]);

  return (
    <div>
      <h3>Frame: {frame}</h3>
      <div
        className="game-ui"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/ui/building_6.png)`,
        }}
      >
        <div id="game-ui-facts">
          Ore: {userState.ore}
          <br />
          Timber: {userState.timber}
        </div>
        <div id="game-ui-buildings">
          {buildableBuildings.map((building, idx) => (
            <div
              key={idx}
              className={`game-ui-building-button-row ${
                selectedContstructableBuilding?.name === building.name &&
                "active"
              }`}
              onClick={() => {
                setSelectedContstructableBuilding(building);
              }}
            >
              <div className="game-ui-building-image-wrapper">
                <div
                  className="game-ui-building-image"
                  style={{
                    width: building.width,
                    height: building.height,
                    backgroundImage: `url(${process.env.PUBLIC_URL}/buildings/${building.src})`,
                  }}
                ></div>
              </div>
              <div className="game-ui-builing-button-info">
                <div className="game-ui-builing-button-name">
                  {building.name}
                </div>
                <div>Cost: {building.cost}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {grid && (
        <Map
          mode={MODE.GAME}
          grid={grid}
          onClick={(y, x) => {}}
          onRightClick={(y, x) => {
            if (selectedUnit) {
              let unitIdx = getUnitIdx(units, selectedUnit);

              if (unitIdx !== undefined) {
                // TODO(Rey): The following unit update code is messy becasue it seems it can be updated while we are iterating over units.
                // We need to make sure we only assign these values before or after we process units.

                units[unitIdx].destination = { x, y };

                let resourceType = GetResourceType(grid[y][x]);

                if (resourceType > 0) {
                  units[unitIdx].currentTick = 0;
                  units[unitIdx].repoPathTraced = false;
                  units[unitIdx].resource.ResourceCount = 0;
                  units[unitIdx].resource.ResourceType = resourceType;
                  units[unitIdx].taskStack = SetNewGatherTaskList(
                    units[unitIdx]
                  );
                  units[unitIdx].path = {
                    coords: [],
                    moveDirection: 0,
                    currentPosition: 0,
                  };
                } else {
                  units[unitIdx].taskStack.push(TASKS.MOVE);
                }

                setActiveCell({ x, y });
              }

              setUnits(units);
            }
          }}
          onUnitClick={(unit) => {
            setSelectedUnit(unit);
          }}
          activeCell={activeCell}
          units={units}
          buildings={buildings}
          constructingBuilding={selectedContstructableBuilding}
          onBuildingConstructClick={(building, x, y) => {
            buildings.push({
              position: {
                x,
                y,
              },
              src: building.src,
              isResource: building.isResource,
              resourceAccepted: building.resourceAccepted,
            });
            setBuildings(buildings);
            setFrame((frame) => frame + 1);
            setSelectedContstructableBuilding(undefined);
          }}
          onBuildingConstructCancel={() => {
            setSelectedContstructableBuilding(undefined);
          }}
        />
      )}
    </div>
  );
}
